/**
 * GlobalFilterBar — top bar with ProjectSelector, TemplateChip,
 * DateRangePicker, media type chips, and DataFreshness indicator.
 *
 * Redesigned in Phase 32 (dashboard-filter-redesign) to replace
 * the old template selector + inline project dropdown + period chips.
 */

import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

import { DataFreshness } from './DataFreshness';
import { DateRangePicker } from './DateRangePicker';
import { ProjectSelector } from './ProjectSelector';
import { TemplateChip } from './TemplateChip';

// ============================================================================
// CONSTANTS
// ============================================================================

const MEDIA_TYPE_OPTIONS: { value: 'all' | 'video' | 'image'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Image' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export interface GlobalFilterBarProps {
  /** Timestamp from latest API response meta.timestamp for freshness display */
  apiTimestamp?: string;
}

export function GlobalFilterBar({ apiTimestamp }: GlobalFilterBarProps) {
  const { filters, setFilter, filterMode, projects } = useDashboardContext();

  return (
    <div>
      {/* Main filter bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-surface border-b border-border-subtle gap-4 flex-wrap">
        {/* Left: Project selector + Template chip */}
        <div className="flex items-center gap-3 min-w-0">
          <ProjectSelector />
          <TemplateChip />
        </div>

        {/* Center: Period (DateRangePicker) + Media type chips */}
        <div className="flex items-center gap-4 flex-shrink-0 flex-wrap">
          {/* Period — DateRangePicker handles preset chips + custom */}
          <div className="flex items-center gap-0.5">
            <span className="text-xs text-secondary mr-1.5">vs</span>
            <DateRangePicker />
          </div>

          {/* Divider */}
          <div className="w-px h-4 bg-border-subtle" />

          {/* Media type chips (unchanged) */}
          <div className="flex items-center gap-0.5">
            {MEDIA_TYPE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilter('mediaType', value)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                  filters.mediaType === value
                    ? 'bg-brand text-inverse'
                    : 'text-secondary hover:text-primary hover:bg-surface-subtle'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Data freshness */}
        <DataFreshness timestamp={apiTimestamp} />
      </div>

      {/* Aggregate mode hint (below filter bar) */}
      {filterMode === 'aggregate' && (
        <div className="px-6 py-1.5 bg-brand/5 border-b border-brand/10 text-xs text-brand">
          Showing aggregated data for {projects.length} projects &middot; Pick a single project to exit compare mode
        </div>
      )}
    </div>
  );
}
